import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink} from '@angular/router';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Paginator} from '../../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../../tools/table-sorter/table-sorter';
import {Asset} from '../../../../../../models/cms/asset';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {AssetService} from '../../../../../../services/cms/asset.service';
import {PaginatorComponent} from '../../../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../../../directives/table-sorter/table-sorter.directive';
import {ConfirmDirective} from '../../../../../../directives/confirm/confirm.directive';
import {IsGrantedDirective} from '../../../../../../directives/is-granted/is-granted.directive';
import {FormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {DropdownSelectComponent} from '../../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {DatePipe} from '@angular/common';
import {ConfirmService} from '../../../../../../services/confirm/confirm.service';
import {AuthService} from '../../../../../../services/auth/auth.service';
import {User} from '../../../../../../models/user';
import {AssetTypes} from '../../../../../../constants/asset-type';
import {UserService} from '../../../../../../services/user/user.service';
import {DocumentService} from '../../../../../../services/document/document.service';
import {ImageService} from '../../../../../../services/image/image.service';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {LangService} from '../../../../../../services/language/lang.service';
import {Language} from '../../../../../../models/language';
import {ListFilters} from '../../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../../services/list-filter/list-filter.service';

@Component({
  selector: 'app-content-library-list',
  imports: [
    RouterLink,
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    ConfirmDirective,
    IsGrantedDirective,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    DatePipe,
  ],
  templateUrl: './content-library-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './content-library-list.component.scss'
})
@IsGranted(['ASSET_VIEW'])
@MetaTitle('Content Library List')
export class ContentLibraryListComponent implements OnInit {
  connectedUser!: User;

  assets: Asset[] = [];
  users: User[] = [];
  products: Product[] = [];
  languages: Language[] = [];
  assetTypes = AssetTypes;

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  filters: ListFilters;

  private nameChange$ = new Subject<void>();

  constructor(
    protected imageService: ImageService,
    private auth: AuthService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private assetService: AssetService,
    private userService: UserService,
    private documentService: DocumentService,
    private confirm: ConfirmService,
    private productService: ProductService,
    private langService: LangService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      lang: 'string[]',
      type: 'string[]',
      userId: 'number[]',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.auth.getConnectedUser().subscribe((user: User) => {
      this.connectedUser = user;
    });

    this.userService.findAll({sort: 'firstname,lastname'}).subscribe((users: User[]) => {
      this.users = users;
    });

    this.productService.findAll({sort: 'name'}).subscribe((products: Product[]) => {
      this.products = products;
    });

    this.langService.getLanguages().subscribe((languages: Language[]) => {
      this.languages = languages;
    });

    this.nameChange$.pipe(debounceTime(300)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  fetchAll(): void {
    const params: any = {...this.filters.toParams()};

    this.loader.startLoading();
    this.assetService.findAll(params, this.paginator, this.tableSorter).subscribe((assets: Asset[]) => {
      this.assets = assets;
      this.loader.stopLoading();
    });
  }

  ownsAsset(asset: Asset): boolean {
    return asset.user?.id === this.connectedUser?.id;
  }

  canDuplicate(asset: Asset): boolean {
    return ['html', 'css', 'js'].includes(asset.type ?? '');
  }

  delete(asset: Asset): void {
    if (asset.id) {
      this.loader.startLoading();
      this.assetService.delete(asset.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Asset has been deleted.');
        this.fetchAll();
      });
    }
  }

  duplicate(asset: Asset): void {
    if (!asset.id || !asset.document?.id) {
      return;
    }

    this.loader.startLoading();
    this.documentService.getContent(asset.document.id).subscribe({
      next: (docContent) => {
        const params: any = {
          name: asset.name + ' (copy)',
          type: asset.type,
          productId: asset.product?.id ?? null,
          lang: asset.lang,
          document: {
            filename: asset.document!.filename,
            data: docContent.content,
          },
        };

        this.assetService.create(params).subscribe({
          next: () => {
            this.loader.stopLoading();
            this.snackbar.success('Asset duplicated successfully.');
            this.fetchAll();
          },
          error: () => {
            this.loader.stopLoading();
          }
        });
      },
      error: () => {
        this.loader.stopLoading();
      }
    });
  }
}
